import {doc, getDoc, setDoc, updateDoc, serverTimestamp, deleteField, query, collection, where } from "firebase/firestore";
import {db} from "./firebase";
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import {useCollectionData} from "react-firebase-hooks/firestore";
import Dinero from "dinero.js";
import {jigpawsDineroFormatted} from "./utils";

// Personalised Christmas Jigsaw Puzzles

export const usePhotobook = (uid, uuid) => {
    const photobookQuery = query(
        collection(db, "photobooks").withConverter(photobookConverter),
        where("uid", "==", uid)
    );

    const [photobooks, photobooksLoading, photobookError] = useCollectionData(
        photobookQuery,
        {
            snapshotListenOptions: {includeMetadataChanges: true},
        }
    );

    if (!photobooksLoading && !photobookError && photobooks.length === 0) {
        createPhotobook(uid);
    }

    const photobook = _.find(photobooks, {"id": uuid});
    // console.log("photobooks", photobooks);

    return [photobook, photobooksLoading, photobookError];
};


export const createPhotobook = async (uid) => {
    const ref = doc(db, "photobooks", uid).withConverter(photobookConverter);
    // console.log("creating photobook document...");
    await setDoc(ref, new Photobook(uid, uid), {merge: true});
};


export const useBasket = (user) => {
    const basketQuery = query(
        collection(db, "baskets").withConverter(basketConverter),
        where("uid", "==", user.uid)
    );

    const [baskets, basketsLoading, basketsError] = useCollectionData(
        basketQuery,
        {
            snapshotListenOptions: {includeMetadataChanges: true},
        }
    );

    if (!basketsLoading && !basketsError && baskets.length === 0) {
        createBasket(user.uid);
    }

    let basket = null;
    if (baskets) {
        basket = baskets[0];
    }
    return [basket, basketsLoading, basketsError];
};


export const createBasket = async (uid) => {
    const ref = doc(db, "baskets", uid).withConverter(basketConverter);
    // console.log("creating basket document...");
    await setDoc(ref, new Basket(uid), {merge: true});
};

export const emptyBasket = async (uid) => {
    const ref = doc(db, "baskets", uid);
    await setDoc(ref, {
        uid: uid,
        uuid: uuidv4(),
        createdAt: serverTimestamp(),
    }, {merge: false});
};

export const createJigpaw = async (uid, uuid, oid, history, path) => {
    const docId = (oid && oid !== uid) ? uid + '_' + uuid : uuid;
    // console.log("createJigpaw...", uid, uuid, oid, "docId", docId);
    const ref = doc(db, "jigpaws", docId).withConverter(jigpawConverter);
    await setDoc(ref, new Jigpaw(uid, uuid, oid), {merge: true});
    const docSnap = await getDoc(ref);

    if (history && path) {
        history.push(path);
    }

     if (docSnap.exists()) {
    //   console.log("Document data:", docSnap.data());
      return docSnap.data();
    } else {
      // doc.data() will be undefined in this case
    //   console.log("No such document!");
    }

};

export const createStockImages = (uid) => {
    // console.log("createStockImages", uid);
    createJigpaw(uid, "stock1", "system");
    createJigpaw(uid, "stock2", "system");
    createJigpaw(uid, "stock3", "system");
    createJigpaw(uid, "stock4", "system");
};

class Jigpaw {
    constructor (uid, uuid, oid, createdAt, deleted, filename, previews, stored, original) {
        this.createdAt = createdAt;
        this.deleted = deleted ? deleted : false;
        this.filename = filename;
        this.oid = oid ? oid : uid;
        this.previews = previews;
        this.stored = stored;
        this.uid = uid;
        this.uuid = uuid;
        this.original = original;
    }

    linkUUID() {
        return (_.get(this, "oid", this.uid) === this.uid) ? this.uuid : `${this.uid}_${this.uuid}`;
    }

    mark_deleted() {
        let doc_id = this.uuid;
        if (this.oid === "system") {
            doc_id = this.uid + "_" + doc_id;
        }
        const ref = doc(db, "jigpaws", doc_id);
        setDoc(ref, {deleted: true,}, {merge: true});
    }
}

// Firestore data converter
export const jigpawConverter = {
    toFirestore: (jigpaw) => {
        return {
            createdAt: jigpaw.createdAt ? jigpaw.createdAt : serverTimestamp(),
            deleted: jigpaw.deleted,
            ...(jigpaw.filename && {filename: jigpaw.filename}),
            oid: jigpaw.oid,
            ...(jigpaw.previews && {filename: jigpaw.previews}),
            ...(jigpaw.stored && {filename: jigpaw.stored}),
            uid: jigpaw.uid,
            uuid: jigpaw.uuid,
            ...(jigpaw.original && {filename: jigpaw.original}),
        };
    },
    fromFirestore: (snapshot, options) => {
        const data = snapshot.data(options);
        return new Jigpaw(data.uid, data.uuid, data.oid, data.createdAt, data.deleted, data.filename, data.previews, data.stored, data.original);
    }
};

const productDisplayCategory = (category) => {
    return {"greeting_card": "Card",
            "journal": "Notebook",
            "photo_tile": "Photo Tile",
            "cushion": "Cushion",
            "photobook": "Photo Book",
            "jigsaw": "Jigsaw",
            "calendar": "Calendar"}[category];
};

export const buildProductSeoUrl = (categorySlug, seoCategorySlug) => {
    const productSlug = productRegistry[categorySlug]?.slug;
    if (!productSlug) return null;

    if (seoCategorySlug === 'personalised') {
        return `personalised-${productSlug}`;
    }
    return `personalised-${seoCategorySlug}-${productSlug}`;
};

export const productSeoCategory = (category) => {
    return {"personalised": "Personalised",
            "dog": "Dog",
            "cat": "Cat",
            "pet": "Pet"}[category];
};
export const productRegistry = {
    'greeting_card': { slug: 'greeting-card',   display: 'Card' },
    'journal':       { slug: 'notebook',         display: 'Notebook' },
    'photo_tile':    { slug: 'photo-tile',       display: 'Photo Tile' },
    'cushion':       { slug: 'cushion',          display: 'Cushion' },
    'photobook':     { slug: 'photo-book',       display: 'Photo Book' },
    'jigsaw':        { slug: 'jigsaw-puzzle',    display: 'Jigsaw' },
}
// export const PRODUCT_URL_SLUGS = {
//     'Card': 'greeting-card',
//     'Notebook': 'notebook',
//     'Photo Tile': 'photo-tile',
//     'Cushion': 'cushion',
//     'Photo Book': 'photo-book',
//     'Jigsaw': 'jigsaw',
// }
export const parseProductSeoCategory = (productId) => {
    const withoutPersonalised = productId.replace("personalised-", "");
    const [first, ...rest] = withoutPersonalised.split('-');
    const isAnimalCategory = !!productSeoCategory(first);
    const productSlug = isAnimalCategory ? rest.join('-') : withoutPersonalised;

    const categorySlug = Object.keys(productRegistry).find(
        key => productRegistry[key].slug === productSlug
    );

    return {
        categorySlug,
        productSlug,
        seoCategorySlug: isAnimalCategory ? first : 'personalised',
        seoCategory: isAnimalCategory ? productSeoCategory(first) : 'Personalised',
    }
}

const productDisplaySKU = (id) => {
    return {
        'matte-greeting-card': '5065013628004',
        'gloss-greeting-card': '5065013628011',
        'plain-journal': '5065013628028',
        'lined-journal': '5065013628035',
        'graph-journal': '5065013628042',
        'photo-tile': '5065013628059',
        'photobook-21x21': '5065013628073',
        'photobook-29x29': '5065013628127',
        'suede-cushion-12x12': '5065013628080',
        'suede-cushion-18x18': '5065013628097',
        'jigsaw-500-50x38': '5065013628103',
        'jigsaw-1000-66x50': '5065013628110',
    }[id]
}
const productDisplayDetails = (category) => {
    return {"greeting_card": "324gsm, Matte or Gloss ",
            "journal": "64 Pages, Lined, Plain or Graph",
            "photo_tile": "20 x 20 cm",
            "cushion": "Available in 2 sizes",
            "photobook": "24 customisable pages",
            "jigsaw": "500 or 1000 pieces",
            "calendar": "Calendar"}[category];
};

// const productDetails = (category) => {
//     if (category === 'notebook') {
//         category = 'journal'
//     }
//     return {
//         'jigsaw':
//             'Create a one-of-a-kind puzzle from your own photo\n' +
//             'Available in 500 piece or 1000 piece sizes\n' +
//             'Vibrant, high-definition colour printing\n' +
//             'Premium quality pieces with a secure fit\n' +
//             'Perfect gift for pet lovers, birthdays, Christmas & anniversaries\n' +
//             'Beautifully presented in a gift box with protective storage pouch\n' +
//             'Handmade to order with free worldwide shipping\n',
//         'cushion':
//             'Available in 30 x 30cm or 46 x 46cm sizes\n' +
//             'Create a one-of-a-kind cushion from your own photo\n' +
//             'Soft, premium faux-suede fabric with a silk finish\n' +
//             'Vibrant, high-definition colour printing\n' +
//             'Removable cover with hidden zip closure\n' +
//             'Machine washable for easy care\n' +
//             'Perfect gift for pet lovers, birthdays & special occasions\n' +
//             'Handmade to order with free shipping in the UK, USA & Europe\n',
//         'photobook':
//             'Available in 21 x 21cm or 29 x 29cm sizes\n' +
//             'Create a beautiful personalised photo book from your favourite memories\n' +
//             '24 fully customisable pages\n' +
//             'Premium hardcover with a matte laminate finish\n' +
//             'Durable library-quality binding built to last\n' +
//             'Professionally printed on high-quality satin-finished paper\n' +
//             'Perfect for pet memories, family albums, birthdays & anniversaries\n' +
//             'Sustainably sourced materials\n' +
//             'Handmade to order with free shipping in the UK, USA & Europe\n',
//         'journal':
//             'Turn your favourite photo into a personalised notebook\n' +
//             'Available with plain, lined or graph paper\n' +
//             'Premium hardback cover with a gloss finish\n' +
//             '64 pages for writing, planning, journaling or sketching\n' +
//             'A unique personalised gift they\'ll use every day\n' +
//             'Perfect for school, work, home or travel\n' +
//             'Handmade to order with free UK shipping\n',
//         'photo_tile':
//             'Turn your favourite photo into beautiful framed wall art\n' +
//             'Premium 20 x 20cm format perfect for walls, shelves & desks\n' +
//             'Black frame crafted for a modern gallery look\n' +
//             'Sharp, vibrant colour reproduction\n' +
//             'Lightweight, durable and built to last\n' +
//             'Ideal for pet portraits, family photos & travel memories\n' +
//             'A unique personalised gift they\'ll treasure forever\n' +
//             'Handmade to order with free UK shipping\n',
//         'greeting_card':
//             'Create a personalised greeting card with your own photo\n' +
//             'Available in matte or gloss finish\n' +
//             'Professionally printed in vibrant, high-definition colour\n' +
//             'Premium heavyweight 324gsm card stock\n' +
//             'Perfect for birthdays, anniversaries, Christmas & special occasions\n' +
//             'Add a personal touch with your favourite pet, family or travel photos\n' +
//             'More than a card — a personalised keepsake they\'ll treasure\n' +
//             'Handmade to order with free UK shipping\n',
//     }[category]
// }
// const productDescriptionLong = (category) => {
//     if (category === 'notebook') {
//         category = 'journal'
//     }
//     return {
//         'jigsaw': 'Use our easy Jigpaws editor to create your personalised puzzle in minutes. Upload your photo, crop and resize it, then preview your design before ordering. It\'s the perfect way to turn your favourite photos into a unique, handmade gift.',
//         'cushion': 'Use our easy-to-use Jigpaws editor to create a personalised cushion in minutes. Simply upload your favourite photo, then crop, resize and position it exactly how you want. Preview your design before ordering to see your favourite photo transformed into a unique, handmade cushion you\'ll treasure for years to come.',
//         'photobook': 'Create a personalised photo book in minutes with our easy-to-use Jigpaws editor. Upload your favourite photos, then crop, resize and arrange them exactly how you want. Preview your design before ordering and see your favourite memories as a beautiful keepsake that\'s perfect for celebrating pets, family, travel and life\'s special moments.',
//         'journal': 'Create a personalised notebook in minutes with our easy-to-use Jigpaws editor. Simply upload your favourite photo, then crop, resize and position it exactly how you want. Preview your design before ordering and transform your favourite pet photo, family memory or special moment into a notebook that\'s uniquely yours.',
//         'photo_tile': 'Create a personalised photo tile in minutes with our easy-to-use Jigpaws editor. Simply upload your favourite photo, then crop, resize and position it exactly how you want. Preview your finished design to see your favourite pet photo, family memory or special moment as a beautiful piece of framed wall art, handmade just for you.',
//         'greeting_card': 'Create a personalised greeting card in minutes with our easy-to-use Jigpaws editor. Simply upload your favourite photo, then crop, resize and position it exactly how you want. Add fun emojis and personal touches before previewing your finished design. Whether it\'s for a birthday, anniversary, thank you or special celebration, create a card that\'s as unique as the person receiving it.',
//     }[category]
// }
export const productDescriptionLong = (category, seoCategory) => {
    if (category === 'notebook') {
        category = 'journal'
    }
    const content = {
        'personalised': {
            'jigsaw': 'Transform your favourite photo into a personalised jigsaw puzzle made especially for you. Upload your image to the easy-to-use Jigpaws editor, crop and position it exactly how you want, then preview your design before ordering. Whether it\'s a family photo, holiday memory, wedding, favourite pet or special celebration, every personalised jigsaw puzzle is handmade to order and designed to become a keepsake you\'ll enjoy for years to come.',
            'cushion': 'Turn your favourite photo into a personalised cushion that\'s made just for you. Upload your image, crop and position it in our easy-to-use editor, then preview your finished design before ordering. From family portraits and wedding photos to travel memories and favourite pets, create a unique cushion that adds a personal touch to your home or makes a thoughtful gift for someone special.',
            'photobook': 'Bring your favourite memories together in a beautifully printed personalised photo book. Upload your photos, arrange them exactly how you want and preview every page before ordering. Whether you\'re celebrating family, travel, weddings, children, pets or life\'s biggest moments, your personalised photo book becomes a keepsake you\'ll treasure forever.',
            'photo_tile': 'Turn your favourite photo into personalised framed wall art in just a few minutes. Upload your image, crop and resize it in the Jigpaws editor, then preview your finished design before ordering. Display family memories, travel photography, wedding moments or favourite pet portraits as modern wall art that brings your home to life.',
            'journal': 'Design a personalised notebook featuring your favourite photo in just a few clicks. Upload your image, customise the layout and preview your finished notebook before ordering. Perfect for work, school, journalling or everyday notes, it\'s a practical keepsake that\'s uniquely yours.',
            'greeting_card': 'Create a personalised greeting card that means more by adding your own favourite photo. Upload your image, customise the design and preview it before ordering. Perfect for birthdays, anniversaries, Christmas, weddings, thank-you messages and every special occasion, it\'s a card they\'ll want to keep long after the celebration.',
        },
        'dog': {
            'jigsaw': 'Turn your favourite dog photo into a personalised jigsaw puzzle made just for you. Upload your image to the easy-to-use Jigpaws editor, crop and resize it, then preview your finished puzzle before ordering. Whether it captures a playful puppy, a much-loved family dog or a special memory, your personalised dog photo puzzle becomes a unique keepsake and a thoughtful gift for any dog lover.',
            'cushion': 'Turn your favourite dog photo into a soft, personalised cushion you can enjoy every day. Simply upload your image to the Jigpaws editor, then crop, resize and position it exactly how you want. Preview your design before ordering and transform a treasured photo of your dog into a unique piece of home décor, a comforting keepsake or a thoughtful personalised gift for a dog lover.',
            'photobook': 'Celebrate your dog\'s story with a personalised photo book filled with your favourite memories. Upload your photos to the Jigpaws editor, then crop, resize and arrange each image exactly how you want. From puppy pictures and family adventures to birthdays, holidays and everyday moments, your personalised dog photo album creates a beautiful keepsake you can revisit for years to come.',
            'photo_tile': 'Turn your favourite dog photo into personalised framed wall art. Upload your image to the Jigpaws editor, then crop, resize and position it to create the perfect composition. Preview your finished design before ordering and transform a favourite puppy portrait, treasured pet memory or characterful snapshot into a modern photo tile made especially for your home.',
            'journal': 'Carry your favourite dog photo wherever you go with a personalised notebook designed by you. Upload your image to the Jigpaws editor, then crop, resize and position it exactly how you want. Whether you use it for notes, planning, journalling or sketching, your personalised dog notebook turns an everyday essential into a unique keepsake featuring your four-legged friend.',
            'greeting_card': 'Make someone smile with a personalised greeting card featuring their favourite dog. Upload your photo to the Jigpaws editor, then crop, resize and position it to create a design that feels completely personal. Add emojis and creative touches before previewing your finished card. Whether it is for a birthday, Christmas, an anniversary or simply to say thank you, a personalised dog card makes every message more memorable.',
        },
        'cat': {
            'jigsaw': 'Turn your favourite cat photo into a personalised jigsaw puzzle made just for you. Upload your image to the easy-to-use Jigpaws editor, crop and resize it, then preview your finished puzzle before ordering. Whether it features a playful kitten, a much-loved family cat or a special memory, your personalised cat photo puzzle becomes a unique keepsake and a thoughtful gift for any cat lover.',
            'cushion': 'Turn your favourite cat photo into a soft, personalised cushion you can enjoy every day. Simply upload your image to the Jigpaws editor, then crop, resize and position it exactly how you want. Preview your design before ordering and transform a treasured photo of your cat into a unique piece of home décor, a comforting keepsake or a thoughtful personalised gift for a cat lover.',
            'photobook': 'Celebrate your cat\'s story with a personalised photo book filled with your favourite memories. Upload your photos to the Jigpaws editor, then crop, resize and arrange each image exactly how you want. From kitten photos and cosy moments to birthdays, holidays and everyday adventures, your personalised cat photo album creates a beautiful keepsake you can revisit for years to come.',
            'photo_tile': 'Turn your favourite cat photo into personalised framed wall art. Upload your image to the Jigpaws editor, then crop, resize and position it to create the perfect composition. Preview your finished design before ordering and transform a favourite kitten portrait, treasured cat memory or characterful snapshot into a modern photo tile made especially for your home.',
            'journal': 'Carry your favourite cat photo wherever you go with a personalised notebook designed by you. Upload your image to the Jigpaws editor, then crop, resize and position it exactly how you want. Whether you use it for notes, planning, journalling or sketching, your personalised cat notebook turns an everyday essential into a unique keepsake featuring your feline friend.',
            'greeting_card': 'Make someone smile with a personalised greeting card featuring their favourite cat. Upload your photo to the Jigpaws editor, then crop, resize and position it to create a design that feels completely personal. Add emojis and creative touches before previewing your finished card. Whether it is for a birthday, Christmas, an anniversary or simply to say thank you, a personalised cat card makes every message more memorable.',
        },
        'pet': {
            'jigsaw': 'Turn your favourite pet photo into a personalised jigsaw puzzle made just for you. Upload your image to the easy-to-use Jigpaws editor, crop and resize it, then preview your finished puzzle before ordering. Whether it\'s a dog, cat, horse, rabbit, bird or another much-loved companion, your personalised pet photo puzzle transforms a favourite memory into a unique keepsake and a thoughtful gift for any pet lover.',
            'cushion': 'Turn your favourite pet photo into a soft, personalised cushion you can enjoy every day. Simply upload your image to the Jigpaws editor, then crop, resize and position it exactly how you want. Preview your design before ordering and transform a treasured photo of your pet into a unique piece of home décor, a comforting keepsake or a thoughtful personalised gift for any pet lover.',
            'photobook': 'Celebrate your pet\'s story with a personalised photo book filled with your favourite memories. Upload your photos to the Jigpaws editor, then crop, resize and arrange each image exactly how you want. From playful moments and family adventures to birthdays, holidays and everyday memories, your personalised pet photo album creates a beautiful keepsake you\'ll treasure for years to come.',
            'photo_tile': 'Turn your favourite pet photo into personalised framed wall art. Upload your image to the Jigpaws editor, then crop, resize and position it to create the perfect composition. Preview your finished design before ordering and transform a favourite pet portrait into a stylish piece of wall art that celebrates the special bond you share.',
            'journal': 'Carry your favourite pet photo wherever you go with a personalised notebook designed by you. Upload your image to the Jigpaws editor, then crop, resize and position it exactly how you want. Whether you use it for notes, planning, journalling or sketching, your personalised pet notebook transforms an everyday essential into a unique keepsake featuring your favourite companion.',
            'greeting_card': 'Make someone smile with a personalised greeting card featuring their favourite pet. Upload your photo to the Jigpaws editor, then crop, resize and position it to create a design that\'s completely unique. Add emojis and personal touches before previewing your finished card. Whether it\'s for a birthday, Christmas, an anniversary, a thank-you or another special occasion, create a card they\'ll want to keep long after the celebration.',
        }
    };
    return (content[seoCategory] || content['personalised'])[category];
}

export const productDetails = (category, seoCategory) => {
    if (category === 'notebook') {
        category = 'journal'
    }
    const content = {
        'personalised': {
            'jigsaw':
                'Create a personalised jigsaw puzzle from your own photo\n' +
                'Available in 500-piece and 1000-piece sizes\n' +
                'Premium-quality puzzle pieces with a secure fit\n' +
                'Vibrant, high-definition colour printing\n' +
                'Beautifully presented in a personalised gift box\n' +
                'Includes a protective storage pouch\n' +
                'Perfect for birthdays, Christmas, anniversaries, weddings and personalised gifts\n' +
                'Handmade to order\n',
            'cushion':
                'Create a personalised cushion from your own photo\n' +
                'Available in 30 × 30cm and 46 × 46cm sizes\n' +
                'Soft premium faux-suede fabric with a luxurious silk finish\n' +
                'Vibrant, high-definition colour printing\n' +
                'Removable cover with hidden zip\n' +
                'Machine washable\n' +
                'Perfect for personalised gifts and home décor\n' +
                'Handmade to order\n',
            'photobook':
                'Create a personalised photo book from your favourite memories\n' +
                'Available in 21 × 21cm and 29 × 29cm sizes\n' +
                '24 fully customisable pages\n' +
                'Premium hardcover with matte laminate finish\n' +
                'Professionally printed on high-quality satin paper\n' +
                'Durable library-quality binding\n' +
                'Sustainably sourced materials\n' +
                'Handmade to order\n',
            'photo_tile':
                'Create personalised wall art from your own photo\n' +
                'Premium 20 × 20cm framed format\n' +
                'Sharp, vibrant colour reproduction\n' +
                'Contemporary gallery-style black frame\n' +
                'Lightweight and easy to display\n' +
                'Perfect for homes, offices and gifts\n' +
                'Handmade to order\n',
            'journal':
                'Create a personalised notebook using your own photo\n' +
                'Available with plain, lined or graph paper\n' +
                'Premium hardback cover with gloss finish\n' +
                '64 pages for writing, planning or sketching\n' +
                'Ideal for work, school, home and travel\n' +
                'A practical personalised gift\n' +
                'Handmade to order\n',
            'greeting_card':
                'Create a personalised greeting card using your own photo\n' +
                'Available with matte or gloss finish\n' +
                'Premium heavyweight 324gsm card stock\n' +
                'Vibrant, high-definition print quality\n' +
                'Perfect for every special occasion\n' +
                'A personalised card and keepsake in one\n' +
                'Handmade to order\n',
        },
        'dog': {
            'jigsaw':
                'Create a personalised jigsaw puzzle from your own dog photo\n' +
                'Available in 500-piece and 1000-piece sizes\n' +
                'Vibrant, high-definition colour printing\n' +
                'Premium-quality puzzle pieces with a secure fit\n' +
                'Perfect for dog lovers, birthdays, Christmas and anniversaries\n' +
                'Beautifully presented in a personalised gift box\n' +
                'Includes a protective storage pouch\n' +
                'Handmade to order\n',
            'cushion':
                'Create a personalised cushion from your own dog photo\n' +
                'Available in 30 × 30cm and 46 × 46cm sizes\n' +
                'Soft, premium faux-suede fabric with a smooth silk finish\n' +
                'Vibrant, high-definition colour printing\n' +
                'Removable cover with a discreet hidden zip\n' +
                'Machine washable for easy care\n' +
                'Perfect for dog lovers, birthdays, Christmas and special occasions\n' +
                'Handmade to order\n',
            'photobook':
                'Create a personalised photo book featuring your dog\n' +
                'Available in 21 × 21cm and 29 × 29cm sizes\n' +
                '24 fully customisable pages\n' +
                'Premium hardcover with a protective matte laminate finish\n' +
                'Durable, library-quality binding\n' +
                'Professionally printed on high-quality satin-finished paper\n' +
                'Perfect for puppy memories, dog adventures and memorial keepsakes\n' +
                'A thoughtful gift for dog owners and pet-loving families\n' +
                'Made using sustainably sourced materials\n' +
                'Handmade to order\n',
            'photo_tile':
                'Turn your favourite dog photo into personalised framed wall art\n' +
                'Premium 20 × 20cm format\n' +
                'Ideal for walls, shelves and desks\n' +
                'Contemporary black frame for a clean gallery-style finish\n' +
                'Sharp, vibrant colour reproduction\n' +
                'Lightweight, durable and easy to display\n' +
                'Perfect for dog portraits, puppy photos and pet memorials\n' +
                'A meaningful personalised gift for any dog lover\n' +
                'Handmade to order\n',
            'journal':
                'Create a personalised notebook using your own dog photo\n' +
                'Available with plain, lined or graph paper\n' +
                'Premium hardback cover with a glossy finish\n' +
                '64 pages for writing, planning, journalling or sketching\n' +
                'Perfect for school, work, home and travel\n' +
                'A practical personalised gift for dog lovers\n' +
                'Ideal for puppy photos, pet portraits and favourite dog memories\n' +
                'Handmade to order\n',
            'greeting_card':
                'Create a personalised greeting card using your own dog photo\n' +
                'Available with a matte or gloss finish\n' +
                'Professionally printed in vibrant, high-definition colour\n' +
                'Premium heavyweight 324gsm card stock\n' +
                'Perfect for birthdays, Christmas, anniversaries and thank-you messages\n' +
                'Add personality with favourite puppy photos and dog portraits\n' +
                'A thoughtful card and keepsake in one\n' +
                'Handmade to order\n',
        },
        'cat': {
            'jigsaw':
                'Create a personalised jigsaw puzzle from your own cat photo\n' +
                'Available in 500-piece and 1000-piece sizes\n' +
                'Vibrant, high-definition colour printing\n' +
                'Premium-quality puzzle pieces with a secure fit\n' +
                'Perfect for cat lovers, birthdays, Christmas and anniversaries\n' +
                'Beautifully presented in a personalised gift box\n' +
                'Includes a protective storage pouch\n' +
                'Handmade to order\n',
            'cushion':
                'Create a personalised cushion from your own cat photo\n' +
                'Available in 30 × 30cm and 46 × 46cm sizes\n' +
                'Soft, premium faux-suede fabric with a smooth silk finish\n' +
                'Vibrant, high-definition colour printing\n' +
                'Removable cover with a discreet hidden zip\n' +
                'Machine washable for easy care\n' +
                'Perfect for cat lovers, birthdays, Christmas and special occasions\n' +
                'Handmade to order\n',
            'photobook':
                'Create a personalised photo book featuring your cat\n' +
                'Available in 21 × 21cm and 29 × 29cm sizes\n' +
                '24 fully customisable pages\n' +
                'Premium hardcover with a protective matte laminate finish\n' +
                'Durable, library-quality binding\n' +
                'Professionally printed on high-quality satin-finished paper\n' +
                'Perfect for kitten memories, cat adventures and memorial keepsakes\n' +
                'A thoughtful gift for cat owners and pet-loving families\n' +
                'Made using sustainably sourced materials\n' +
                'Handmade to order\n',
            'photo_tile':
                'Turn your favourite cat photo into personalised framed wall art\n' +
                'Premium 20 × 20cm format\n' +
                'Ideal for walls, shelves and desks\n' +
                'Contemporary black frame for a clean gallery-style finish\n' +
                'Sharp, vibrant colour reproduction\n' +
                'Lightweight, durable and easy to display\n' +
                'Perfect for cat portraits, kitten photos and pet memorials\n' +
                'A meaningful personalised gift for any cat lover\n' +
                'Handmade to order\n',
            'journal':
                'Create a personalised notebook using your own cat photo\n' +
                'Available with plain, lined or graph paper\n' +
                'Premium hardback cover with a glossy finish\n' +
                '64 pages for writing, planning, journalling or sketching\n' +
                'Perfect for school, work, home and travel\n' +
                'A practical personalised gift for cat lovers\n' +
                'Ideal for kitten photos, cat portraits and favourite memories\n' +
                'Handmade to order\n',
            'greeting_card':
                'Create a personalised greeting card using your own cat photo\n' +
                'Available with a matte or gloss finish\n' +
                'Professionally printed in vibrant, high-definition colour\n' +
                'Premium heavyweight 324gsm card stock\n' +
                'Perfect for birthdays, Christmas, anniversaries and thank-you messages\n' +
                'Add personality with favourite kitten photos and cat portraits\n' +
                'A thoughtful card and keepsake in one\n' +
                'Handmade to order\n',
        },
        'pet': {
            'jigsaw':
                'Create a personalised jigsaw puzzle from your own pet photo\n' +
                'Available in 500-piece and 1000-piece sizes\n' +
                'Vibrant, high-definition colour printing\n' +
                'Premium-quality puzzle pieces with a secure fit\n' +
                'Perfect for pet lovers, birthdays, Christmas and anniversaries\n' +
                'Beautifully presented in a personalised gift box\n' +
                'Includes a protective storage pouch\n' +
                'Handmade to order\n',
            'cushion':
                'Create a personalised cushion from your own pet photo\n' +
                'Available in 30 × 30cm and 46 × 46cm sizes\n' +
                'Soft, premium faux-suede fabric with a smooth silk finish\n' +
                'Vibrant, high-definition colour printing\n' +
                'Removable cover with a discreet hidden zip\n' +
                'Machine washable for easy care\n' +
                'Perfect for pet lovers, birthdays, Christmas and special occasions\n' +
                'Suitable for dogs, cats, horses and other pets\n' +
                'Handmade to order\n',
            'photobook':
                'Create a personalised photo book featuring your pet\n' +
                'Available in 21 × 21cm and 29 × 29cm sizes\n' +
                '24 fully customisable pages\n' +
                'Premium hardcover with a protective matte laminate finish\n' +
                'Durable, library-quality binding\n' +
                'Professionally printed on high-quality satin-finished paper\n' +
                'Perfect for pet memories, puppy and kitten albums, horse lovers and memorial keepsakes\n' +
                'A thoughtful gift for pet owners and animal lovers\n' +
                'Made using sustainably sourced materials\n' +
                'Handmade to order\n',
            'photo_tile':
                'Turn your favourite pet photo into personalised framed wall art\n' +
                'Premium 20 × 20cm format\n' +
                'Ideal for walls, shelves and desks\n' +
                'Contemporary black frame for a clean gallery-style finish\n' +
                'Sharp, vibrant colour reproduction\n' +
                'Lightweight, durable and easy to display\n' +
                'Perfect for dogs, cats, horses, rabbits, birds and other pets\n' +
                'A meaningful personalised gift for every pet lover\n' +
                'Handmade to order\n',
            'journal':
                'Create a personalised notebook using your own pet photo\n' +
                'Available with plain, lined or graph paper\n' +
                'Premium hardback cover with a glossy finish\n' +
                '64 pages for writing, planning, journalling or sketching\n' +
                'Perfect for school, work, home and travel\n' +
                'A practical personalised gift for pet lovers\n' +
                'Suitable for dogs, cats, horses and all beloved pets\n' +
                'Handmade to order\n',
            'greeting_card':
                'Create a personalised greeting card using your own pet photo\n' +
                'Available with a matte or gloss finish\n' +
                'Professionally printed in vibrant, high-definition colour\n' +
                'Premium heavyweight 324gsm card stock\n' +
                'Perfect for birthdays, Christmas, anniversaries and special occasions\n' +
                'Ideal for dogs, cats, horses and every type of pet\n' +
                'More than a card – a personalised keepsake they\'ll treasure\n' +
                'Handmade to order\n',
        }
    };
    return (content[seoCategory] || content['personalised'])[category];
}

const productDisplayVariant = (id) => {
    return {
        'matte-greeting-card': 'Matte',
        'gloss-greeting-card': 'Gloss',
        'lined-journal': 'Lined pages',
        'plain-journal': 'Plain pages',
        'graph-journal': 'Graph pages',
        'photo-tile': 'Photo title',
        'suede-cushion-12x12': '30x30cm',
        'suede-cushion-18x18': '46x46cm',
        'jigsaw-500-50x38': '500 pieces, 50x38cm',
        'jigsaw-1000-66x50': '1000 pieces, 66x50cm',
        'photobook-21x21': '21x21cm',
        'photobook-29x29': '29x29cm',
    }[id]
}


// not used with firebase client
class Product {
    constructor (category, preview, comingSoon, currency, description, description1, description2,  discounts, display_order, id, name, price, promo, promoInReceipt, quantity_max, quantity_min, variant) {
        this.category = category;
        this.preview = preview;
        this.comingSoon = comingSoon;
        this.currency = currency;
        this.description = description;
        this.description1 = description1;
        this.description2 = description2;
        this.discounts = discounts;
        this.display_order = display_order;
        this.id = id;
        this.name = name;
        this.price = price;
        this.promo = promo;
        this.promoInReceipt = promoInReceipt;
        this.quantity_max = quantity_max;
        this.quantity_min = quantity_min;
        this.variant = variant;
        this.displayVariant = productDisplayVariant(this.id);
        this.displayCategory = productDisplayCategory(this.category);
        this.productDisplaySKU = productDisplaySKU(this.id);
        this.displayDetails = productDisplayDetails(this.category);
        this.details = productDetails(this.category);
        this.detailsLong = productDescriptionLong(this.category);
        // this.displayType = this.name.toUpperCase();
        this.displayType = this.name;
    }

    discountForQuantity(quantity) {
        if (this.discounts) {
            for (let i = 0; i < this.discounts.length; i++) {
                const discountRange = this.discounts[i];
                if (quantity >= discountRange.min && quantity <= discountRange.max && discountRange.discount > 0) {

                    const price = this.priceForQuantity(quantity);
                    const discount = discountRange.discount * price;
                    // console.log("price", price, "discount", discount, "negative", -discount);

                    return {
                        'amount': -discount,
                        'text': discountRange.text,
                        'type': 'product'
                    };
                }
            }
        }
        return null;
    }

    priceForQuantity(quantity) {
        return quantity * this.price;
    }
}

export const productConverter = {
    fromApi: (data) => {
        return new Product(data.category, data.preview, data.comingSoon, data.currency, data.description, data.description1, data.description2, data.discounts, data.display_order, data.id, data.name, data.price, data.promo, data.promoInReceipt, data.quantity_max, data.quantity_min, data.variant);
    }
};

class Country {
    constructor (name, code) {
        this.name = name;
        this.code = code;
    }
}

export const countryConverter = {
    fromApi: (data) => {
        return new Country(data.name, data.isoCode)
    }
}


class Item {
    constructor(createdAt, jigpawId, oid, product, quantity, preview, category) {
         this.createdAt = createdAt;
         this.jigpawId = jigpawId;
         this.oid = oid;
         this.product = product;
         this.quantity = quantity;
         this.preview = preview;
         this.category = category;
    }

    displayCategory() {
        return productDisplayCategory(this.category);
    }
}


class Basket {
    constructor(uid, uuid, items, createdAt, discount_code, merging) {
        this.uid = uid;
        this.uuid = uuid || uuidv4();
        this.items = _.transform(items || {}, (result, item, key) => {
            result[key] = new Item(item.createdAt, item.jigpawId, item.oid, item.product, item.quantity, item.preview, item.category);
        }, {});
        this.createdAt = createdAt;
        this.discount_code = discount_code;
        this.merging = merging || false;
    }

    makeItemKey(jigpawId, productId) {
        return `${jigpawId}${productId}`;
    }

    hasItem(jigpawId, productId) {
        const key = this.makeItemKey(jigpawId, productId);
        return this.items.hasOwnProperty(key);
    }

    containsPhotobook() {
        return this.items.hasOwnProperty("photobook");
    }

    hasItems() {
        return !_.isEmpty(this.items);
    }

    hasPhotobook() {
        return _.has(this.items, "photobook");
    }

    addPhotobook(photobook) {
        // const preview = _.get(jigpaw, `previews.${product.category}`)
        const ref = doc(db, "baskets", this.uid);
        setDoc(ref, {
            items: {
                photobook: {
                    createdAt: serverTimestamp(),
                    jigpawId: photobook.frontPage.jigpawId,
                    oid: photobook.frontPage.oid,
                    product: photobook.productId,
                    quantity: 1,
                    preview: "photobook",
                    category: "photobook",
                }
            }
        }, {merge: true});
    }

    updatePhotobookFrontPage(jigpaw) {
        const ref = doc(db, "baskets", this.uid);
        setDoc(ref, {
            items: {
                photobook: {
                    jigpawId: jigpaw.uuid,
                    oid: jigpaw.oid,
                }
            }
        }, {merge: true});
    }

    updatePhotobookProduct(productId) {
        const ref = doc(db, "baskets", this.uid);
        updateDoc(ref, {
            [`items.photobook.product`]: productId
        });
    }

    updatePhotobookQuantity(quantity) {
        const ref = doc(db, "baskets", this.uid);
        updateDoc(ref, {
            [`items.photobook.quantity`]: parseInt(quantity, 10)
        });
    }

    removePhotobook() {
        const ref = doc(db, "baskets", this.uid);
        updateDoc(ref, {
            [`items.photobook`]: deleteField()
        });
    }

    addItem(jigpaw, product, quantity) {

        const itemKey = this.makeItemKey(jigpaw.uuid, product.id);
        const preview = _.get(jigpaw, `previews.${product.category}`)
        const ref = doc(db, "baskets", this.uid);

        setDoc(ref, {
            items: {
                [itemKey]: {
                    createdAt: serverTimestamp(),
                    jigpawId: jigpaw.uuid,
                    oid: jigpaw.oid,
                    product: product.id,
                    quantity: quantity,
                    ...(preview && {preview: preview}),
                    category: product.category
                }
            }
        }, {merge: true});
    }

    updateItem(jigpawId, productId, quantity) {
        const itemKey = this.makeItemKey(jigpawId, productId);
        const ref = doc(db, "baskets", this.uid);
        updateDoc(ref, {
            [`items.${itemKey}.quantity`]: parseInt(quantity, 10)
        });
    }

    removeItem(jigpawId, productId) {
        const itemKey = this.makeItemKey(jigpawId, productId);
        const ref = doc(db, "baskets", this.uid);
        updateDoc(ref, {
            [`items.${itemKey}`]: deleteField()
        });
    }

    itemQuantity(jigpawId, productId) {
        const itemKey = this.makeItemKey(jigpawId, productId);
        let quantity = 1;
        try {
            quantity = this.items[itemKey].quantity
        }
        catch(e) {}
        return quantity
    }

    totalPrice(products, basketDiscount) {
        // console.log("basket.totalPrice: basketDiscount", basketDiscount)
        let groupedItemPrices = []; // basketItems on iOS
        let discounts = [];
        const items_list = Object.values(this.items)
        // console.log(items_list)
        if (items_list) {
            const grouped_items = _.groupBy(items_list, 'product');
            _.forEach(products, (product) => {
                const items = grouped_items[product.id]
                if (items) {
                    let quantityOfProduct = 0
                    const sortedItems = _.sortBy(items, 'createdAt');
                    _.forEach(sortedItems, (item) => {
                        quantityOfProduct += item.quantity;
                        groupedItemPrices.push(product.priceForQuantity(item.quantity))
                    })

                    if (!basketDiscount) {
                        const discountForQuantity = product.discountForQuantity(quantityOfProduct);
                        if (discountForQuantity) {
                            // console.log("discountForQuantity", discountForQuantity)
                            discounts.push(discountForQuantity);
                        }
                    }
                }
            })

            const subtotal = _.sum(groupedItemPrices);
            if (basketDiscount) {
                discounts.push(
                    {
                        'type': 'basket',
                        'text': `${basketDiscount.code} ${basketDiscount.percentage}% off`,
                        'amount': -(basketDiscount.percentage / 100) * subtotal
                    }
                )
            }
            const discountTotal = _.sumBy(discounts,'amount');

            return {
                'discounts': discounts,
                'subtotal': subtotal,
                'total': subtotal + discountTotal
            }
        }
        return {}
    }

    totalQuantity() {
        return _.sumBy(_.values(this.items), "quantity")
    }

    removeDiscount() {
        const ref = doc(db, "baskets", this.uid);
        return updateDoc(ref, {
            'discount_code': deleteField()
        });
    }
}

export const basketConverter = {
    toFirestore: (basket) => {
        return {
            uid: basket.uid,
            uuid: basket.uuid,
            ...(basket.items && {items: basket.items}),
            createdAt: basket.createdAt ? basket.createdAt : serverTimestamp(),
            ...(basket.discount_code && {discount_code: basket.discount_code}),
            merging: basket.merging
        };
    },
    fromFirestore: (snapshot, options) => {
        const data = snapshot.data(options);
        return new Basket(data.uid, data.uuid, data.items, data.createdAt, data.discount_code, data.merging);
    }
};


// class Page {
//     constructor(jigpawId, oid) {
//          this.jigpawId = jigpawId;
//          this.oid = oid;
//     }
//
//     // displayCategory() {
//     //     return productDisplayCategory(this.category);
//     // }
//     toDict() {
//         return {
//             jigpawId: this.jigpawId,
//             oid: this.oid,
//         }
//     }
// }

class Photobook {
    constructor(id, uid, uuid, frontPage, pages, productId, createdAt) {
        this.id = id;
        this.uid = uid;
        this.uuid = uuid || uuidv4();
        this.frontPage = frontPage;
        this.pages = pages;
        this.productId = productId || _.head(Photobook.sizes).productId;
        // this.pages = _.transform(pages || {}, (result, page, key) => {
        //     result[key] = new Page(page.jigpawId, page.oid);
        // }, {});
        this.createdAt = createdAt;
    }

    static pageKeys() {
        return _.times(24, (num)=> {
            return `page${num+1}`
        })
    }

    static pageKeysIncludingBlanks() {
        return _.times(25, (num)=> {
            return `page${num}`
        })
    }

    static isFrontBlank(pageNumber) {
        return (pageNumber === 0) ? true : false;
    }

    static isBackBlank(pageNumber) {
        return (pageNumber === 25) ? true : false;
    }

    static isPage(pageNumber) {
        const pageKey = `page${pageNumber}`;
        return _.includes(Photobook.pageKeys(), pageKey);
    }

    static sizes = [
        {
            display: "21x21cm",
            productId: "photobook-21x21",
        },
        {
            display: "29x29cm",
            productId: "photobook-29x29",
        }
    ]

    hasValidProductId() {
        return _.find(Photobook.sizes, {productId: this.productId}) !== undefined;
    }

    cost(photobooks, currency) {
        // console.log("photobooks", photobooks);
        const photobook = _.find(photobooks, {id: this.productId});
        const amount = _.get(photobook, "price", "2000");
        return jigpawsDineroFormatted(Dinero({amount: amount, currency: currency}));
    }

    updateProductId(value, basket) {
        const ref = doc(db, "photobooks", this.uid);
        setDoc(ref, {
            productId: value
        }, {merge: true});

        if (basket && basket.containsPhotobook()) {
            // console.log("got here...")
            basket.updatePhotobookProduct(value);
        } else {
            // console.log("got here2...")
        }
    }


    updateFrontPage(jigpaw, basket) {
        const ref = doc(db, "photobooks", this.uid);
        setDoc(ref, {
            frontPage: {
                jigpawId: jigpaw.uuid,
                oid: jigpaw.oid,
            }
        }, {merge: true});

        if (basket && basket.containsPhotobook()) {
            basket.updatePhotobookFrontPage(jigpaw);
        }
    }

    deleteFrontPage() {
        const ref = doc(db, "photobooks", this.uid);
        updateDoc(ref, {
            [`frontPage`]: deleteField()
        });
    }

    getPage(pageNumber) {
        return _.get(this.pages, `page${pageNumber}`)
    }

    updatePage(pageNumber, jigpaw) {

        const pageKey = `page${pageNumber}`;
        if (!Photobook.isPage(pageNumber)) {
            // console.log("shouldn't get here...")
        }

        const ref = doc(db, "photobooks", this.uid);
        setDoc(ref, {
            pages: {
                [pageKey]: {
                    jigpawId: jigpaw.uuid,
                    oid: jigpaw.oid,
                }
            }
        }, {merge: true});
    }

    updatePages(pages) {
        const data = _.transform(pages, (result, page, index) => {
            const pageKey = `page${index+1}`;
            result[pageKey] = (page) ? page : deleteField()
        }, {})
        const ref = doc(db, "photobooks", this.uid)
        // console.log(data);
        setDoc(ref, {pages: data}, {merge: true});
    }

    deletePage(pageNumber) {
        const pageKey = `page${pageNumber}`;
        const ref = doc(db, "photobooks", this.uid);
        updateDoc(ref, {
            [`pages.${pageKey}`]: deleteField()
        });
    }

    hasFrontPage() {
        return _.has(this.frontPage, "jigpawId")
    }

    hasAllPages() {
        return _.isEqual(_.sortBy(_.keys(this.pages)), _.sortBy(Photobook.pageKeys()))
    }

    isComplete() {
        //check we have a jigpaw image for each page.
        return this.hasFrontPage() && this.hasAllPages()
    }
}

export const photobookConverter = {
    toFirestore: (photobook) => {
        return {
            uid: photobook.uid,
            uuid: photobook.uuid,
            ...(photobook.frontPage && {frontPage: photobook.frontPage}),
            ...(photobook.pages && {pages: photobook.pages}),
            productId: photobook.productId ? photobook.productId : _.head(Photobook.sizes).productId,
            createdAt: photobook.createdAt ? photobook.createdAt : serverTimestamp(),
        };
    },
    fromFirestore: (snapshot, options) => {
        const data = snapshot.data(options);
        // Keep original productId (including invalid ones like "photobook-14x14")
        // The UI will handle forcing users to select valid sizes
        const productId = data.productId || _.head(Photobook.sizes).productId;
        return new Photobook(snapshot.id, data.uid, data.uuid, data.frontPage, data.pages, productId, data.createdAt);
    }
};

export {Photobook}